import API from '../api/client';

/**
 * Workspace management service for handling user, role, and permission operations.
 */
export const workspaceService = {
  // ============= User Management =============

  /**
   * Get all users in the workspace
   * @param {string} search - Optional search query
   * @returns {Promise} List of users
   */
  async getUsers(search = '') {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    return await API.get(`/workspace/users${params}`);
  },

  /**
   * Get current user's workspace information
   * @returns {Promise} Current user data
   */
  async getCurrentUser() {
    return await API.get('/workspace/users/me');
  },

  /**
   * Invite a new user to the workspace
   * @param {Object} userData - User data {email, name, role_id}
   * @returns {Promise} Invitation response
   */
  async inviteUser(userData) {
    return await API.post('/workspace/users/invite', userData);
  },

  /**
   * Update user details (role, status, etc.)
   * @param {string} userId - User ID to update
   * @param {Object} updates - Updates to apply
   * @returns {Promise} Updated user data
   */
  async updateUser(userId, updates) {
    return await API.put(`/workspace/users/${userId}`, updates);
  },

  /**
   * Delete a user from the workspace
   * @param {string} userId - User ID to delete
   * @returns {Promise} Deletion response
   */
  async deleteUser(userId) {
    return await API.delete(`/workspace/users/${userId}`);
  },

  /**
   * Deactivate a user (soft delete)
   * @param {string} userId - User ID to deactivate
   * @returns {Promise} Updated user data
   */
  async deactivateUser(userId) {
    return await API.patch(`/workspace/users/${userId}/deactivate`);
  },

  // ============= Role Management =============

  /**
   * Get all roles (system + custom)
   * @returns {Promise} List of roles
   */
  async getRoles() {
    return await API.get('/workspace/roles');
  },

  /**
   * Create a custom role
   * @param {Object} roleData - Role data {role_name, description, permissions}
   * @returns {Promise} Created role data
   */
  async createRole(roleData) {
    return await API.post('/workspace/roles', roleData);
  },

  /**
   * Update a role
   * @param {string} roleId - Role ID to update
   * @param {Object} updates - Updates to apply
   * @returns {Promise} Updated role data
   */
  async updateRole(roleId, updates) {
    return await API.put(`/workspace/roles/${roleId}`, updates);
  },

  /**
   * Delete a custom role
   * @param {string} roleId - Role ID to delete
   * @returns {Promise} Deletion response
   */
  async deleteRole(roleId) {
    return await API.delete(`/workspace/roles/${roleId}`);
  },

  // ============= Permission Management =============

  /**
   * Get all available permissions grouped by category
   * @returns {Promise} Permissions grouped by category
   */
  async getPermissions() {
    return await API.get('/workspace/permissions');
  },

  // ============= Notification Settings =============

  /**
   * Get notification settings for the workspace
   * @returns {Promise} Notification settings
   */
  async getNotificationSettings() {
    return await API.get('/workspace/notifications');
  },

  /**
   * Update notification email list
   * @param {Array} emails - List of email addresses
   * @returns {Promise} Updated notification settings
   */
  async updateNotificationEmails(emails) {
    return await API.put('/workspace/notifications', { notification_emails: emails });
  },

  /**
   * Add a single email to notification list
   * @param {string} email - Email address to add
   * @returns {Promise} Updated notification settings
   */
  async addNotificationEmail(email) {
    return await API.post('/workspace/notifications/emails', { email });
  },

  /**
   * Remove an email from notification list
   * @param {string} email - Email address to remove
   * @returns {Promise} Updated notification settings
   */
  async removeNotificationEmail(email) {
    return await API.delete(`/workspace/notifications/emails/${encodeURIComponent(email)}`);
  },

  // ============= Helper Methods =============

  /**
   * Check if the current user has a specific permission
   * @param {Array} userPermissions - User's permission list
   * @param {string} permission - Permission to check
   * @returns {boolean} True if user has permission
   */
  hasPermission(userPermissions, permission) {
    if (!userPermissions || !Array.isArray(userPermissions)) return false;
    return userPermissions.includes('*') || userPermissions.includes(permission);
  },

  /**
   * Check if the current user has any of the specified permissions
   * @param {Array} userPermissions - User's permission list
   * @param {Array} permissions - Permissions to check
   * @returns {boolean} True if user has any permission
   */
  hasAnyPermission(userPermissions, permissions) {
    if (!userPermissions || !Array.isArray(userPermissions)) return false;
    if (userPermissions.includes('*')) return true;
    return permissions.some(perm => userPermissions.includes(perm));
  },

  /**
   * Check if the current user has all of the specified permissions
   * @param {Array} userPermissions - User's permission list
   * @param {Array} permissions - Permissions to check
   * @returns {boolean} True if user has all permissions
   */
  hasAllPermissions(userPermissions, permissions) {
    if (!userPermissions || !Array.isArray(userPermissions)) return false;
    if (userPermissions.includes('*')) return true;
    return permissions.every(perm => userPermissions.includes(perm));
  },

  /**
   * Format user status for display
   * @param {string} status - User status
   * @returns {Object} Formatted status with label and color
   */
  formatUserStatus(status) {
    const statusMap = {
      active: { label: 'Active', color: 'success' },
      inactive: { label: 'Inactive', color: 'warning' },
      pending_activation: { label: 'Pending', color: 'info' }
    };
    return statusMap[status] || { label: status, color: 'default' };
  },

  /**
   * Format role for display
   * @param {Object} role - Role object
   * @returns {Object} Formatted role
   */
  formatRole(role) {
    return {
      ...role,
      isSystem: role.is_system_role,
      badgeColor: role.is_system_role ? 'primary' : 'secondary',
      canEdit: !role.is_system_role,
      canDelete: !role.is_system_role && role.user_count === 0
    };
  }
};